/**
 * Typed wrappers around the three surprise-link endpoints
 * (view / open / claim). Reuses the redeem feature's CouponApiError so the
 * page can branch on the same stable backend error codes.
 *
 *   GET  /coupons/surprise/:token        → public (personalizes if authed)
 *   POST /coupons/surprise/:token/open   → authed; draws + reserves a prize
 *   POST /coupons/surprise/:token/claim  → authed; commits the held prize
 */
import { AxiosError } from 'axios';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../config/api';
import { CouponApiError } from '../../redeem/services/couponApi';
import type { CouponErrorCode, ClaimReceipt, ClaimResponse } from '../../redeem/types';
import type { SurpriseView, SurpriseOpenResult } from '../types';

function toCouponApiError(err: unknown): CouponApiError {
  const ax = err as AxiosError<{ error?: { code?: string; message?: string } }>;
  if (!ax?.response) {
    return new CouponApiError('NETWORK', ax?.message || 'Network error');
  }
  const body = ax.response.data;
  const code = (body?.error?.code as CouponErrorCode | undefined) || 'UNKNOWN';
  const message = body?.error?.message || ax.message || 'Request failed';
  return new CouponApiError(code, message, ax.response.status);
}

/** Public view. apiClient attaches the JWT automatically when present, so a
 *  signed-in viewer's already-claimed / reserved prize comes back too. */
export async function getSurpriseView(token: string): Promise<SurpriseView> {
  try {
    const { data } = await apiClient.get<SurpriseView>(API_ENDPOINTS.COUPON_SURPRISE_VIEW(token));
    return data;
  } catch (err) {
    throw toCouponApiError(err);
  }
}

/** Authed. Draws + reserves a random gift and reveals the real prize. */
export async function openSurprise(token: string): Promise<SurpriseOpenResult> {
  try {
    const { data } = await apiClient.post<SurpriseOpenResult>(
      API_ENDPOINTS.COUPON_SURPRISE_OPEN(token),
    );
    return data;
  } catch (err) {
    throw toCouponApiError(err);
  }
}

/** Authed. Commits the held gift (code resolved server-side). */
export async function claimSurprise(token: string): Promise<ClaimReceipt> {
  try {
    const { data } = await apiClient.post<ClaimResponse>(
      API_ENDPOINTS.COUPON_SURPRISE_CLAIM(token),
    );
    return data.receipt;
  } catch (err) {
    throw toCouponApiError(err);
  }
}
